import type { ActorEvent, CustomViewConfig, RecordLikeProtocol, RichTextContentProtocol } from '@lighthouse/core'
import { BLOCK_SIZE } from '@lighthouse/core'
import type { ContainerLayoutContextValue, FlowLayoutNode } from '@lighthouse/shared'
import { ContainerLayoutProvider, useContainerLayoutContext, ViewEmptyState } from '@lighthouse/shared'
import type { BreakPointSize } from '@lighthouse/tools'
import React, { forwardRef, memo, useMemo } from 'react'

import { useEffectiveAnchorId, useViewBlockContext } from '../../context'
import { useScrollToAnchorWhenHashMatch } from '../../hooks/useScrollToAnchorWhenHashMatch'
import { MonthCalendarView } from './MonthCalendarView'

const MONTH_CALENDAR_SIZE_MAP: Record<string, number> = {
    [BLOCK_SIZE.sm]: 90,
    [BLOCK_SIZE.md]: 110,
    [BLOCK_SIZE.lg]: 130,
    [BLOCK_SIZE.xl]: 150
}

interface CustomViewMonthCalendarBlockProps {
    currentMonth: Date
    node: FlowLayoutNode
    blockWidth: number
    breakPoint?: BreakPointSize
    readonly?: boolean
    onRenderRichText: (v: RichTextContentProtocol) => string
    onRecordClick?: (id: string, actorEvent: ActorEvent, record?: RecordLikeProtocol) => Promise<void>
}

export const CustomViewMonthCalendarBlock = memo(
    forwardRef<HTMLDivElement, CustomViewMonthCalendarBlockProps>((props, ref) => {
        const { currentMonth, node, blockWidth, readonly, onRenderRichText, onRecordClick } = props
        const { blockData, dataSource } = useViewBlockContext()
        const { id, title, config } = blockData
        const { breakPoint: baseBreakPointConfig, calendarStartDateField, calendarEndDateField, anchor } = config as CustomViewConfig
        const { anchorId } = anchor || {}
        const effectiveAnchorId = useEffectiveAnchorId(anchorId)
        useScrollToAnchorWhenHashMatch(effectiveAnchorId, anchor)
        const { viewLayout, viewStyles, layout, emptyView } = baseBreakPointConfig || {}
        const { gap, size = BLOCK_SIZE.md, adaptive } = viewLayout || {}

        const showEmpty = !dataSource

        // adaptive ON → no minimum width (columns divide container equally)
        // adaptive OFF → use size-mapped minimum width
        const itemWidth = useMemo(() => {
            if (adaptive) return undefined
            return MONTH_CALENDAR_SIZE_MAP[size] ?? MONTH_CALENDAR_SIZE_MAP[BLOCK_SIZE.md]
        }, [adaptive, size])

        const { level } = useContainerLayoutContext()

        const contextValue: ContainerLayoutContextValue = useMemo(() => {
            return {
                parentId: id,
                level: level + 1,
                size: {
                    width: {
                        size: blockWidth,
                        unit: 'px'
                    },
                    height: {
                        size: 'auto'
                    }
                },
                layout: {
                    ...layout
                }
            }
        }, [id, level, blockWidth, layout])

        if (showEmpty) {
            return <ViewEmptyState id={effectiveAnchorId} config={emptyView} onRenderRichText={onRenderRichText} />
        }

        return (
            <ContainerLayoutProvider value={contextValue}>
                <MonthCalendarView
                    id={effectiveAnchorId}
                    currentMonth={currentMonth}
                    node={node}
                    title={title}
                    gap={gap?.size || 0}
                    readonly={readonly}
                    itemWidth={itemWidth}
                    calendarStartDateField={calendarStartDateField}
                    calendarEndDateField={calendarEndDateField}
                    viewStyles={viewStyles}
                    onRecordClick={onRecordClick}
                />
            </ContainerLayoutProvider>
        )
    })
)
